import { useCallback, useEffect, useRef, useState } from "react";
import { modelsApi } from "../../../../../API/axiosInstance";
import { SESSION_META_ENDPOINT, SESSION_META_URL, POST_PAGE_SIZE } from "./constants";

// ─────────────────────────────────────────────────────────────────────────────
// usePosts — REST with infinite scroll
// ─────────────────────────────────────────────────────────────────────────────
export function usePosts({ sessionId, dataType, sessionType, enabled }) {
  const [posts,       setPosts]       = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error,       setError]       = useState(null);
  const [page,        setPage]        = useState(1);
  const [totalPages,  setTotalPages]  = useState(1);
  const [totalCount,  setTotalCount]  = useState(0);
  const [hasMore,     setHasMore]     = useState(false);

  const initAbortRef   = useRef(null);
  const isFetchingMore = useRef(false);

  // Always-fresh values for stable callbacks
  const sessionIdRef   = useRef(sessionId);
  const dataTypeRef    = useRef(dataType);
  const sessionTypeRef = useRef(sessionType);
  const pageRef        = useRef(page);
  const totalPagesRef  = useRef(totalPages);
  sessionIdRef.current   = sessionId;
  dataTypeRef.current    = dataType;
  sessionTypeRef.current = sessionType;
  pageRef.current        = page;
  totalPagesRef.current  = totalPages;

  const fetchInitial = useCallback(async (sid, type) => {
    initAbortRef.current?.abort();
    const controller = new AbortController();
    initAbortRef.current = controller;

    setLoading(true); setError(null); setPosts([]); setPage(1);

    try {
      const params = { sessionId: sid, data_type: type, page: 1, page_size: POST_PAGE_SIZE };
      if (type === "processed") params.type = sessionTypeRef.current;
      const { data } = await modelsApi.get(SESSION_META_ENDPOINT, {
        signal: controller.signal,
        params,
      });
      const tp = data.total_pages ?? 1;
      setPosts(data.data ?? []);
      setTotalPages(tp);
      setTotalCount(data.total_count ?? 0);
      setHasMore(1 < tp);
    } catch (e) {
      if (e.name !== "CanceledError" && e.name !== "AbortError")
        setError(e.message ?? "Unknown error");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadMore = useCallback(async () => {
    if (isFetchingMore.current) return;
    const nextPage = pageRef.current + 1;
    if (nextPage > totalPagesRef.current) return;

    isFetchingMore.current = true;
    setLoadingMore(true);

    try {
      const params = {
        sessionId: sessionIdRef.current,
        data_type: dataTypeRef.current,
        page: nextPage,
        page_size: POST_PAGE_SIZE,
      };
      if (dataTypeRef.current === "processed") params.type = sessionTypeRef.current;
      const { data } = await modelsApi.get(SESSION_META_ENDPOINT, { params });
      const tp = data.total_pages ?? 1;
      setPosts((prev) => [...prev, ...(data.data ?? [])]);
      setTotalPages(tp);
      setTotalCount(data.total_count ?? 0);
      setPage(nextPage);
      setHasMore(nextPage < tp);
    } catch (_) {
      // silent — user can scroll back to retry
    } finally {
      isFetchingMore.current = false;
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled || !sessionId) { initAbortRef.current?.abort(); return; }
    fetchInitial(sessionId, dataType);
    return () => initAbortRef.current?.abort();
  }, [sessionId, dataType, enabled, fetchInitial]);

  const retry = useCallback(
    () => fetchInitial(sessionIdRef.current, dataTypeRef.current),
    [fetchInitial],
  );

  return { posts, loading, loadingMore, error, hasMore, totalCount, loadMore, retry };
}

// ─────────────────────────────────────────────────────────────────────────────
// useStream — pure SSE, zero REST calls
// streamedIds uses a ref to avoid O(n) Set spread on every message.
// ─────────────────────────────────────────────────────────────────────────────
export function useStream({ sessionId, dataType, sessionType, enabled }) {
  const [posts,        setPosts]        = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState(null);
  const [totalCount,   setTotalCount]   = useState(0);
  const [connected,    setConnected]    = useState(false);
  const [newLiveCount, setNewLiveCount] = useState(0);

  const esRef          = useRef(null);
  const streamedIdsRef = useRef(new Set());

  useEffect(() => {
    if (esRef.current) { esRef.current.close(); esRef.current = null; }

    if (!enabled || !sessionId) { setConnected(false); return; }

    setPosts([]); setError(null); setNewLiveCount(0);
    streamedIdsRef.current = new Set();
    setTotalCount(0); setConnected(false); setLoading(true);

    const urlParams = new URLSearchParams({ sessionId, data_type: dataType, stream: "true" });
    if (dataType === "processed") urlParams.append("type", sessionType);
    const es = new EventSource(`${SESSION_META_URL}?${urlParams}`, { withCredentials: true });
    esRef.current = es;

    es.onopen = () => { setConnected(true); setLoading(false); };

    es.addEventListener("message", (e) => {
      try {
        const post = JSON.parse(e.data);
        setPosts((prev) => [post, ...prev.filter((p) => p._id !== post._id)]);
        streamedIdsRef.current.add(post._id);
        setNewLiveCount((c) => c + 1);
        setTotalCount((c) => c + 1);
      } catch (err) { console.error("SSE parse error:", err); }
    });

    es.addEventListener("end", () => {
      setConnected(false); setLoading(false);
      es.close(); esRef.current = null;
    });

    es.addEventListener("error", (e) => {
      try { const p = JSON.parse(e.data); setError(p?.error ?? "Stream error"); }
      catch (_) { /* plain network error */ }
      setConnected(false); setLoading(false);
    });

    es.onerror = () => { setConnected(false); setLoading(false); };

    return () => { es.close(); esRef.current = null; setConnected(false); };
  }, [sessionId, dataType, sessionType, enabled]);

  const retry = useCallback(() => {
    if (esRef.current) { esRef.current.close(); esRef.current = null; }
    setConnected(false); setLoading(true); setError(null);

    const retryParams = new URLSearchParams({ sessionId, data_type: dataType, stream: "true" });
    if (dataType === "processed") retryParams.append("type", sessionType);
    const es = new EventSource(`${SESSION_META_URL}?${retryParams}`, { withCredentials: true });
    esRef.current = es;

    es.onopen = () => { setConnected(true); setLoading(false); };
    es.addEventListener("message", (e) => {
      try {
        const post = JSON.parse(e.data);
        setPosts((prev) => [post, ...prev.filter((p) => p._id !== post._id)]);
        streamedIdsRef.current.add(post._id);
        setNewLiveCount((c) => c + 1);
        setTotalCount((c) => c + 1);
      } catch (err) { console.error("SSE parse error:", err); }
    });
    es.addEventListener("end", () => { setConnected(false); setLoading(false); es.close(); esRef.current = null; });
    es.onerror = () => { setConnected(false); setLoading(false); };
  }, [sessionId, dataType, sessionType]);

  return {
    posts, loading, error, totalCount, connected, newLiveCount,
    streamedIds: streamedIdsRef.current,
    retry,
  };
}
